import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { LineChart, BarChart } from 'echarts/charts';
import { TooltipComponent, LegendComponent, GridComponent, DataZoomComponent, ToolboxComponent } from 'echarts/components';

echarts.use([
  CanvasRenderer,
  LineChart,
  BarChart,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DataZoomComponent,
  ToolboxComponent
]);

function getThemeBackgroundColor() {
  const mainElement = document.querySelector('main');
  if (mainElement) {
    const computedStyle = window.getComputedStyle(mainElement);
    const backgroundColor = computedStyle.backgroundColor;
    
    if (backgroundColor && backgroundColor !== 'rgba(0, 0, 0, 0)' && backgroundColor !== 'transparent') {
      return backgroundColor;
    }
  }

  const vAppElement = document.querySelector('.v-application');
  if (vAppElement) {
    const computedStyle = window.getComputedStyle(vAppElement);
    const backgroundColor = computedStyle.backgroundColor;
    
    if (backgroundColor && backgroundColor !== 'rgba(0, 0, 0, 0)' && backgroundColor !== 'transparent') {
      return backgroundColor;
    }
  }

  const htmlElement = document.documentElement;
  const isDarkTheme = htmlElement.classList.contains('dark');
  
  if (isDarkTheme) {
    return '#213542';
  } else {
    return '#F5F5F5';
  }
}

function getChartFontColor() {
  // Try to get text color from computed styles first
  const mainElement = document.querySelector('main');
  if (mainElement) {
    const computedStyle = window.getComputedStyle(mainElement);
    const color = computedStyle.color;
    
    if (color && color !== 'rgba(0, 0, 0, 0)' && color !== 'transparent') {
      return color;
    }
  }

  // Fallback: Detect theme based on background color
  const backgroundColor = getThemeBackgroundColor();
  
  // If background is dark, use white text; if light, use black text
  if (backgroundColor === '#213542' || backgroundColor.includes('rgb(33, 53, 66)')) {
    return '#FFFFFF';
  } else {
    return '#000000';
  }
}

function truncateLegendText(text, maxWidth) {
  /**
   * Truncates legend text to fit within max width
   * @param {string} text - Legend text to truncate
   * @param {number} maxWidth - Maximum width in pixels (approx)
   * @returns {string} Truncated text with ellipsis if needed
   */
  // Assume 8px per character average
  const maxChars = Math.floor(maxWidth / 8);
  if (text.length > maxChars) {
    return text.substring(0, Math.max(1, maxChars - 3)) + '...';
  }
  return text;
}

function groupLegendByColor(series) {
  /**
   * Groups series by color and creates combined legend names with deduplicated status names
   * Keeps all series but updates the first series name for each color group
   * @param {Array} series - Array of series from chart option
   * @returns {Object} Object with groupedLegend data and updated series array
   */
  const colorGroups = {};
  const colorOrder = [];

  // Group series by their color, maintaining order
  series.forEach((ser, index) => {
    const color = ser.itemStyle?.color || ser.lineStyle?.color || '#999';
    if (!colorGroups[color]) {
      colorGroups[color] = [];
      colorOrder.push(color);
    }
    colorGroups[color].push({ name: ser.name, index });
  });

  // Create legend data and update series names
  const groupedLegend = [];
  const updatedSeries = [...series];

  colorOrder.forEach(color => {
    const items = colorGroups[color];

    // Get unique status names for this color group, filtering out color codes
    const uniqueNames = [...new Set(items.map(item => item.name))]
      .filter(name => !name.startsWith('#')); // Filter out hex color codes

    // Use status names if available, otherwise keep the color for now
    const combinedName = uniqueNames.length > 0 ? uniqueNames.join(', ') : color;

    // Add combined name to legend (only once per color) with explicit color
    groupedLegend.push({
      name: combinedName,
      icon: 'circle',
      itemStyle: { color: color }
    });

    // Update ONLY the first series name in each color group for legend matching
    const firstIndex = items[0].index;
    updatedSeries[firstIndex].name = combinedName;

    // Hide other series with same color from legend
    items.slice(1).forEach(item => {
      updatedSeries[item.index].legendHoverLink = false;
    });
  });

  return { groupedLegend, updatedSeries };
}

function calculateLegendHeight(legendData, chartWidth) {
  /**
   * Estimates the height needed for the legend based on content and chart width
   * @param {Array} legendData - Array of legend items
   * @param {number} chartWidth - Width of the chart in pixels
   * @returns {number} Estimated height in pixels
   */
  if (!legendData || legendData.length === 0) {
    return 0;
  }

  // Use 85% of chart width for legend items
  const availableWidth = chartWidth * 0.85;
  const avgCharWidth = 8;
  const iconAndPadding = 35; // icon + spacing between items
  
  let totalWidth = 0;
  let rows = 1;
  
  legendData.forEach(item => {
    const itemWidth = item.name.length * avgCharWidth + iconAndPadding;
    
    if (totalWidth + itemWidth > availableWidth && totalWidth > 0) {
      // Start a new row if item doesn't fit
      rows++;
      totalWidth = itemWidth;
    } else {
      totalWidth += itemWidth;
    }
  });
  
  const itemHeight = 28; // pixels per row (including padding)
  return rows * itemHeight + 15; // 15px extra bottom padding
}

export function downloadChartAsPNG(chart, filename = 'chart.png', seriesData = null) {
  if (!chart) return;

  const originalOption = chart.getOption();
  const backgroundColor = getThemeBackgroundColor();
  const chartFontColor = getChartFontColor();

  // Base dimensions
  const chartWidth = chart.getWidth();
  const chartHeight = chart.getHeight();

  // Create a temporary hidden div for off-screen rendering with legend
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  tempDiv.style.width = `${chartWidth}px`;
  document.body.appendChild(tempDiv);

  // Initialize a new ECharts instance on the temp div
  const tempChart = echarts.init(tempDiv);

  // Create download option with legend
  const downloadOption = JSON.parse(JSON.stringify(originalOption)); // Deep copy

  // Build legend data and update series names
  let legendData = [];
  let filteredSeries = [];

  // If seriesData is provided, filter series and build legend
  if (seriesData && Array.isArray(seriesData) && seriesData.length > 0) {
    // Build a map of color -> display name from seriesData
    const colorToDisplayName = new Map();
    seriesData.forEach(sd => {
      if (sd.name) {
        // Extract color from the name if it's a hex color, or use it as-is
        // The seriesData contains objects with name and titles
        const displayName = sd.titles && Array.isArray(sd.titles) && sd.titles.length > 0
          ? [...new Set(sd.titles)].join(', ')
          : sd.name;
        // We need to match by the original series color
        colorToDisplayName.set(sd.name.toLowerCase(), displayName);
      }
    });

    // Filter and update series based on seriesData (match by color)
    filteredSeries = [];
    downloadOption.series.forEach((series) => {
      const seriesColor = (series.itemStyle?.color || series.lineStyle?.color || '').toLowerCase();

      // Find matching seriesData entry by color
      const matchingSeriesData = seriesData.find(sd => {
        const sdColor = (sd.color || '').toLowerCase();
        return sdColor === seriesColor;
      });

      if (matchingSeriesData) {
        const uniqueTitles = matchingSeriesData.titles && Array.isArray(matchingSeriesData.titles)
          ? [...new Set(matchingSeriesData.titles)]
          : [];

        const displayName = uniqueTitles.length > 0
          ? uniqueTitles.join(', ')
          : matchingSeriesData.name;

        filteredSeries.push({
          ...series,
          name: displayName
        });
      }
    });

    // Now apply color grouping to the filtered series
    if (filteredSeries.length > 0) {
      const { groupedLegend, updatedSeries } = groupLegendByColor(filteredSeries);
      legendData = groupedLegend;
      filteredSeries = updatedSeries;
    }
  } else {
    // Extract series names from the chart for legend and group by color
    if (downloadOption.series && Array.isArray(downloadOption.series)) {
      const { groupedLegend, updatedSeries } = groupLegendByColor(downloadOption.series);
      legendData = groupedLegend;
      filteredSeries = updatedSeries;
    }
  }

  // Update downloadOption series to filtered series
  downloadOption.series = filteredSeries;

  // Truncate legend text to fit chart width  
  const maxItemWidth = chartWidth;
  legendData = legendData.map(item => ({
    ...item,
    name: truncateLegendText(item.name, maxItemWidth)
  }));

  // Update series names to match truncated legend names
  downloadOption.series = downloadOption.series.map((series, index) => {
    if (legendData[index]) {
      return { ...series, name: legendData[index].name };
    }
    return series;
  });

  // Calculate legend height based on actual chart width
  const legendHeight = calculateLegendHeight(legendData, chartWidth);
  const totalHeight = chartHeight + legendHeight + 20; // 20px extra padding
  tempDiv.style.height = `${totalHeight}px`;

  // Add legend to the download option
  if (legendData.length > 0) {
    downloadOption.legend = [{
      orient: 'horizontal',
      bottom: 10,
      left: 'center',
      data: legendData,
      textStyle: {
        color: chartFontColor,
        fontSize: 12
      },
      itemWidth: 10,
      itemHeight: 10,
      itemGap: 15,
      pageButtonItemGap: 5,
      pageButtonGap: 5,
      pageButtonPosition: 'end'
    }];
  }

  // Adjust grid to make space for the legend
  if (downloadOption.grid) {
    const gridBottomPx = legendHeight + 30;
    if (Array.isArray(downloadOption.grid)) {
      downloadOption.grid[0].bottom = gridBottomPx;
    } else {
      downloadOption.grid.bottom = gridBottomPx;
    }
  }

  // Hide dataZoom (bottom slider bar) in download
  if (downloadOption.dataZoom) {
    if (Array.isArray(downloadOption.dataZoom)) {
      downloadOption.dataZoom = downloadOption.dataZoom.map(zoom => ({
        ...zoom,
        show: false
      }));
    } else {
      downloadOption.dataZoom.show = false;
    }
  }

  // Set background color and disable animation
  downloadOption.backgroundColor = backgroundColor;
  downloadOption.animation = false;

  // Set the option on the temp chart
  tempChart.setOption(downloadOption);

  // Force resize to ensure proper layout
  tempChart.resize();

  // Add a small delay to ensure rendering completes
  setTimeout(() => {
    // Generate the image URL
    const url = tempChart.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: backgroundColor
    });

    // Clean up: dispose the temp chart and remove div
    tempChart.dispose();
    document.body.removeChild(tempDiv);

    // Trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
  }, 100);
}

export function formatTimeValue(value) {
  if (value === null || value === undefined || value === 'N/A') {
    return 'N/A';
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return 'N/A';
  }

  if (numValue < 1) {
    const hours = numValue * 24;
    if (hours < 1) {
      const minutes = hours * 60;
      if (minutes < 1) {
        const seconds = minutes * 60;
        return `${seconds.toFixed(1)}s`;
      }
      return `${minutes.toFixed(1)}m`;
    }
    return `${hours.toFixed(1)}h`;
  }

  return `${numValue.toFixed(1)}d`;
}